import {_, React, observer, observable, computed, action, useLocalStore, classNames, logJS, pluralize, Fragment, utils} from '@api'
import {makeStyles, Theme, createStyles} from '@material-ui/core/styles'
import {TreeItem, TreeItemProps, TreeView} from '@material-ui/lab'
import {StoresInstance, Workspace} from '@stores'
import {useVipLocalStore, VipLocalStore,CatalogLibrariesStore, useCatalogLibrariesStore} from '@modules'
import {AssignableLibraryField} from './'
import * as css from './CatalogLibrariesTreeView.css'
import {SvgIconProps, Typography} from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'

/// region Store

export const librariesTreeItemIdPrefix = `treeItem_`

export class CatalogLibrariesTreeViewStore extends VipLocalStore {
  constructor(stores: StoresInstance,public catalogLibrariesStore: CatalogLibrariesStore) {
    super(stores)
  }

  @computed get expandedItems() {
    const {settings: {catalogLibrariesPanel: {sections: sectionSettings}}} = this.catalog
    return [..._.keys(sectionSettings).filter(k => sectionSettings[k].open && k.startsWith(librariesTreeItemIdPrefix))]
  }

  @computed get treeItemSections() {
    const {settings: {catalogLibrariesPanel: {sections: sectionSettings}}} = this.catalog
    return _.keys(sectionSettings).filter(id => id.startsWith(librariesTreeItemIdPrefix))
  }

  @action onTreeNodeToggle = (e, expandedNodes: Array<string>) => {
    const {settings: {catalogLibrariesPanel: {sections: sectionSettings}}} = this.catalog,
      {treeItemSections} = this

    treeItemSections.forEach(k => sectionSettings[k].open = false)

    expandedNodes.filter(id => id.startsWith(librariesTreeItemIdPrefix)).forEach(id => {
      if (!sectionSettings[id]) {
        sectionSettings[id] = {open: true}
      } else {
        sectionSettings[id].open = true
      }
    })
  }
}

export const CatalogLibrariesTreeViewStoreContext = React.createContext<CatalogLibrariesTreeViewStore>(null)
export const useCatalogLibrariesTreeViewStore = () => React.useContext(CatalogLibrariesTreeViewStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const CatalogLibrariesTreeView = observer<MyProps>(function CatalogLibrariesTreeView({className, ...props}) {
  const catalogLibrariesStore = useCatalogLibrariesStore(),
    {libraryByWorkspace} = catalogLibrariesStore,
    store = useVipLocalStore((ws) => new CatalogLibrariesTreeViewStore(ws, catalogLibrariesStore)),
    {expandedItems, onTreeNodeToggle} = store

  // logJS(orderedLibraries)

  return (
    <CatalogLibrariesTreeViewStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        <TreeView
          className={css.treeRoot}
          expanded={expandedItems}
          onNodeToggle={onTreeNodeToggle}
          defaultCollapseIcon={<ArrowDropDownIcon/>}
          defaultExpandIcon={<ArrowRightIcon/>}
          defaultEndIcon={<ArrowRightIcon onClick={utils.eatMouseEvent} className={css.disabled}/>}
        >
          {_.keys(libraryByWorkspace).map((id, i) => <WorkspaceLibrariesTreeItem id={id} key={i}/>)}
        </TreeView>
      </div>
    </CatalogLibrariesTreeViewStoreContext.Provider>)
})

/// endregion

declare module 'csstype' {
  interface Properties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string
  color?: string
  labelIcon?: any
  count?: React.ReactNode
  labelText: string
};

const useTreeItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    expanded: {},
    labelIcon: {
      marginRight: theme.spacing(1)
    },
    labelText: {
      fontWeight: 'inherit',
      flexGrow: 1
    }
  })
)

function StyledTreeItem(props: StyledTreeItemProps) {
  const classes = useTreeItemStyles()
  const {labelText, labelIcon: LabelIcon, count, color, bgColor, ...other} = props

  return (
    <TreeItem
      data-id={other.id}
      label={
        <div className={css.labelRoot}>
          {LabelIcon && <LabelIcon color='inherit' className={css.labelIcon}/>}
          <Typography variant='body1' className={css.labelText}>
            {labelText}
          </Typography>
          <Typography className={css.count} variant='caption' color='inherit'>
            {count}
          </Typography>
        </div>
      }
      style={{
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor
      } as any}
      classes={{
        root: css.treeItem,
        content: css.content,
        iconContainer: css.iconContainer,
        expanded: classes.expanded,
        selected: css.selected,
        group: css.group,
        label: css.label
      }}
      {...other}
    />
  )
}

const WorkspaceLibrariesTreeItem = observer<{ id: string }>(function WorkspaceLibrariesTreeItem({id: workspaceId}) {
  const {libraryByWorkspace, workspaces} = useCatalogLibrariesStore(),
    ws = workspaces.wrapperById[workspaceId],
    libraries = libraryByWorkspace[workspaceId]

  if (_.isEmpty(libraries)) {
    return null
  }

  const libraryItemTreeNodes = libraries.map(l => (
      <StyledTreeItem
        nodeId={l.id}
        labelText={l.label}
        key={l.id}
        data-id={l.id}
        count={_.isNumber(l.count) ? l.count.toString() : null}
        onClick={() => l.navigateTo()}
      />))

  return (<Fragment>
    {!ws || _.keys(libraryByWorkspace).length === 1 || libraryItemTreeNodes.length === 1 ? libraryItemTreeNodes :
    <StyledTreeItem
      nodeId={`${librariesTreeItemIdPrefix}${workspaceId}`}
      data-workspace={workspaceId}
      className={css.hasChildren}
      labelText={ws.config.title}
      labelIcon={null}
      // count={libraries.length}
    >
      {libraryItemTreeNodes}
      {false && <TreeItemsForWorkspace workspace={ws}/>}
    </StyledTreeItem>}
  </Fragment>)
});

const TreeItemsForWorkspace = observer<{ workspace: Workspace }>(function TreeItemsForWorkspace({workspace}) {
  const {visibleFieldsBySection} = useCatalogLibrariesStore(),
    {section} = workspace,
    fields = section ? visibleFieldsBySection[section.id] : []

  return _.isEmpty(fields) ? null : (
    <StyledTreeItem nodeId={section.id} labelText='Fields' count={fields.length}>
      {fields.map((f, i) => <AssignableLibraryField key={f?.field ?? i} field={f} className={css.field}/>)}
    </StyledTreeItem>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/sidebar/CatalogLibrariesPanel/CatalogLibrariesTreeView.tsx